import React, { useEffect } from "react"
import {
	Grid
} from "@material-ui/core"
import { useHistory } from "react-router-dom"

import Notification from "@/components/Notification"

import ChatGlobalStateProvider, { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useGlobalStateStore } from "@/store/GlobalState"
import useDidMount from "@/hooks/useDidMount"
import useSocket from "@/hooks/useSocket"
import useThrottledSound from "@/hooks/useThrottledSound"
import useActiveWindowListener from "@/hooks/useActiveWindowListener"
import useChat from "@/hooks/useChat"
import useSettings from "@/hooks/useSettings"
import useQueryFilters from "@/hooks/useQueryFilters"
import useWABAChat from "@/@integrations/WABA/hooks/useWABAChat"
import useOpenChatByPhone from "@/hooks/useOpenChatByPhone"

import { Attendance, IChat } from "@/protocols/channel"
import { Client } from "@/protocols/clientCatalog"

import { getPageFavicon, changePageFavicon } from "@/utils/node"
import { getFilledCircleWithTextInside } from "@/utils/image"

import ChatListPanel from "@/pages/Attendance/Chat/ChatListPanel"
import ConversationPanel from "@/pages/Attendance/Chat/ConversationPanel"
import { INBOX_OPEN_CHAT_BY_PHONE_EVENT } from "@/pages/Attendance/boardNavigationConstants"
import newMessageSound from "@/assets/sounds/new-message.mp3"

import { colors } from "@/styles/colors"
import useStyles from "@/pages/Attendance/Chat/styles"
import { filterUnreadedChats } from "@/utils/filterUnreadMessages"
import HardCoded from "@/services/HardCoded"

const initialPageTitle = "Inbox"
const initialPageFavicon = getPageFavicon()?.href || ""

const Chat: React.FC = () => {
	const socket = useSocket()
	const notificationThrottledSound = useThrottledSound(newMessageSound)
	const chatMethods = useChat()
	const classes = useStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()
	const globalStateStore = useGlobalStateStore()
	const activeWindowListener = useActiveWindowListener()
	const { userSettings } = useSettings()
	const queryFilter = useQueryFilters()
	const history = useHistory()

	const wabaChat = useWABAChat()
	const { openChat, startChatByClientIdFromBackend } = useOpenChatByPhone()

	const unreadChats = filterUnreadedChats(chatGlobalStateStore.chat.list)

	const currentNewUnreadChats = unreadChats.filter(chat => chatMethods.isQueueChat(chat))
	const totalUnreadChats = currentNewUnreadChats.length

	const makeVisualNotification = () => {
		const newTitle = `(${totalUnreadChats}) ${initialPageTitle}`

		const isTitleAlreadySet = newTitle === initialPageTitle

		if (totalUnreadChats && !isTitleAlreadySet) {
			const newFavicon = getFilledCircleWithTextInside({
				circleColor: colors.gray[950],
				fontColor: colors.gray[0],
				text: `${totalUnreadChats}`
			})

			document.title = newTitle
			changePageFavicon(newFavicon)
		} else {
			document.title = initialPageTitle
			changePageFavicon(initialPageFavicon)
		}
	}

	const makeAudioNotification = () => {
		notificationThrottledSound.playSound(3000)
	}

	const hasActiveAttendantOrTeamFilter = (teamIds: number[], userIds: number[]): boolean => {
		if (HardCoded.checkFeatureFlag("canUseAccurateAttendantFilterLogic")) {
			return teamIds.length > 0 || userIds.length > 0
		}

		return teamIds.length > 0 && userIds.length > 0
	}

	const isCurrentUserResponsibleForAttendance = (attendance: Pick<Attendance, "assignTeamId" | "assignUserId" | "assignmentQueueType" | "userId">) => {
		const teamIds = chatGlobalStateStore.chatListPanel.chatListFilter.current?.teams.map((team) => team.id)
		const userIds = chatGlobalStateStore.chatListPanel.chatListFilter.current.attendants.map((user) => user.id)
		const isAttendanceAssignedToGeneralQueue = attendance?.assignmentQueueType === "General"
		const isAttendanceAssignedToCurrentUser = attendance?.assignUserId === globalStateStore?.user?.id
		const isCurrentUserAttendance = attendance?.userId === globalStateStore?.user?.id
		const isFilteringTeamQueueAttendance = teamIds.includes(attendance?.assignTeamId as number)

		const hasFilters = hasActiveAttendantOrTeamFilter(teamIds, userIds)
		const assignAttendanceId = attendance.assignUserId || attendance.userId
		const isFilteringAttendanceChats = userIds.includes(assignAttendanceId as number)
		const isAttendanceAssignedToCurrentUserTeam = globalStateStore?.user?.valid_teams.includes(Number(attendance?.assignTeamId))

		return isAttendanceAssignedToGeneralQueue || isAttendanceAssignedToCurrentUser || isAttendanceAssignedToCurrentUserTeam || isFilteringTeamQueueAttendance || isCurrentUserAttendance || isFilteringAttendanceChats || !hasFilters
	}

	/**
	 * Refactor of the isCurrentUserResponsibleForAttendance function to address an issue where a sound
	 * notification was being triggered in the inbox of the attendant who transferred a attendance because
	 * the receiving attendant had no filters applied.
	 * To fix this, we duplicate the function, removing the hasFilters condition from the function's return.
	 */
	const isCurrentUserResponsibleForAssignedAttendance = (attendance: Pick<Attendance, "assignTeamId" | "assignUserId" | "assignmentQueueType" | "userId">) => {
		const teamIds = chatGlobalStateStore.chatListPanel.chatListFilter.current?.teams.map((team) => team.id)
		const userIds = chatGlobalStateStore.chatListPanel.chatListFilter.current.attendants.map((user) => user.id)
		const isAttendanceAssignedToGeneralQueue = attendance?.assignmentQueueType === "General"
		const isAttendanceAssignedToCurrentUser = attendance?.assignUserId === globalStateStore?.user?.id
		const isCurrentUserAttendance = attendance?.userId === globalStateStore?.user?.id
		const isFilteringTeamQueueAttendance = teamIds.includes(attendance?.assignTeamId as number)

		const assignAttendanceId = attendance.assignUserId || attendance.userId
		const isFilteringAttendanceChats = userIds.includes(assignAttendanceId as number)
		const isAttendanceAssignedToCurrentUserTeam = globalStateStore?.user?.valid_teams.includes(Number(attendance?.assignTeamId))

		return isAttendanceAssignedToGeneralQueue || isAttendanceAssignedToCurrentUser || isAttendanceAssignedToCurrentUserTeam || isFilteringTeamQueueAttendance || isCurrentUserAttendance || isFilteringAttendanceChats
	}

	const validateShouldNotifyAttendanceAssignment = (assignedChat: IChat) => {
		const currentAttendantSettings = userSettings.getUserSettings()

		const receiveSoundNotificationConfig = currentAttendantSettings?.notifications?.soundNotification

		const attendantResponsibleForAttendance =
			isCurrentUserResponsibleForAssignedAttendance(assignedChat.attendance)

		return receiveSoundNotificationConfig && attendantResponsibleForAttendance
	}

	const canUpdateWaitingChats = (attendance: Pick<Attendance, "assignTeamId" | "assignUserId" | "assignmentQueueType" | "userId" | "status">) => {
		const isWaitingChat = attendance?.status === "waiting"
		const assignedQueueType = attendance.assignmentQueueType === "General"

		return (isWaitingChat && isCurrentUserResponsibleForAttendance(attendance)) || assignedQueueType
	}

	type ContactBlockChangeInput = {
		client: { id: number }
		isBlocked: boolean
		successMessage: string
	}

	function handleContactBlockChange ({
		client,
		isBlocked,
		successMessage
	}: ContactBlockChangeInput) {
		const clientToUpdate = chatGlobalStateStore.client.getById(client.id)
		const hasContacts = clientToUpdate?.contacts

		if (!hasContacts) return

		const updatedContacts = clientToUpdate.contacts?.map(contact => {
			const isChannelTypeWaba = contact.channel_type === "waba"

			if (isChannelTypeWaba) {
				return {
					...contact,
					data: {
						...contact.data,
						is_blocked: isBlocked
					}
				}
			}

			return contact
		})

		chatGlobalStateStore.client.updateById(client.id, {
			contacts: updatedContacts
		})
		Notification.success({ message: successMessage })
	}

	const isEventOfUnloadedChat = (chatId: number): boolean => {
		return HardCoded.canPaginateBotChats() && !chatGlobalStateStore.chat.existsById(chatId)
	}

	const setupSocket = async () => {
		socket.onMessageStatusChanged(async updatedMessage => {
			if (isEventOfUnloadedChat(updatedMessage.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(updatedMessage.inboxChannelChatId)

			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				chatGlobalStateStore.message.updateById(updatedMessage.id, {
					status: updatedMessage.status,
					...(updatedMessage.status === "sent" && { createdAt: updatedMessage.createdAt }),
					...(Boolean(updatedMessage.content) && { content: updatedMessage.content }),
					...(updatedMessage.error && { inboxChannelChatMessageLog: { error: updatedMessage.error } })
				})
			}
		})

		socket.onMessageDeleted(async message => {
			if (isEventOfUnloadedChat(message.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(message.inboxChannelChatId)

			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				chatGlobalStateStore.message.updateById(message.id, {
					content: "",
					deletedAt: String(new Date())
				})
			}
		})

		socket.onMessageReaction(async reaction => {
			if (isEventOfUnloadedChat(reaction.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(reaction.inboxChannelChatId)

			if (chat) {
				chatGlobalStateStore.message.updateMessageReactionByMessageId(reaction)
			}
		})

		socket.onEditedMessageReceived(async message => {
			if (isEventOfUnloadedChat(message.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(message.inboxChannelChatId)

			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				chatGlobalStateStore.message.updateById(message.id, {
					content: message.content,
					caption: message.caption,
					extraData: {
						editedMessage: message.extraData?.editedMessage
					}
				})
			}
		})

		socket.onClientDataChanged(async client => {
			const chatClient = chatGlobalStateStore.client.getById(client.id)

			if (chatClient) {
				chatGlobalStateStore.client.updateById(client.id, {
					name: client.name,
					nickname: client.nickname,
					picture_key: client.picture_key,
					picture_url: client.picture_url
				})
			}
		})

		socket.onBoardStageCardContactValueUpdated(async boardStageCardContactValueUpdated => {
			const chatClient = chatGlobalStateStore.client.getById(boardStageCardContactValueUpdated.clientId)

			if (chatClient) {
				chatGlobalStateStore.client.updateById(boardStageCardContactValueUpdated.clientId, {
					cardInfoExtraData: {
						contactValueInCents: boardStageCardContactValueUpdated.contactValueInCents
					}
				})
			}
		})

		socket.onBoardStageCardResponsibleUserUpdated(async boardStageCardResponsibleUserUpdated => {
			const chatClient = chatGlobalStateStore.client.getById(boardStageCardResponsibleUserUpdated.clientId)

			if (chatClient) {
				chatGlobalStateStore.client.updateById(boardStageCardResponsibleUserUpdated.clientId, {
					account_manager_user_id: boardStageCardResponsibleUserUpdated.responsibleUser.id
				})
			}
		})

		socket.onNewChat(async newChat => {
			const isChatWithoutAttendance = newChat.attendance?.status !== "active"
			const isChatWithAttendance = newChat.attendance?.status === "active"
			const isChatAttendanceOwnedByCurrentUser = newChat.attendance?.userId === globalStateStore?.user?.id
			const isChatWithAttendanceThatCanBeShown = isChatWithAttendance && isChatAttendanceOwnedByCurrentUser

			const canSaveChat = isChatWithoutAttendance || isChatWithAttendanceThatCanBeShown

			const othersTabPagination = chatGlobalStateStore.chat.othersTabPagination
			const isChatNotLoaded = !chatGlobalStateStore.chat.existsById(newChat.id)

			if (canSaveChat && othersTabPagination.isEnabled && isChatNotLoaded && othersTabPagination.isPaginatedStatus(newChat.status)) {
				othersTabPagination.registerNewChat(newChat.status)
				return
			}

			if (canSaveChat) {
				chatGlobalStateStore.chat.add(newChat)
			}
		})

		socket.onNewAssignedChat(async newChat => {
			const isChatWithNoAttendant = newChat.attendance?.status !== "active"
			const isChatOnTeamFilter = chatMethods.isAssignedToTeamFilter(newChat)
			const isChatWithAttendantThatCanBeShown = isChatWithNoAttendant && (isCurrentUserResponsibleForAttendance(newChat.attendance) || isChatOnTeamFilter)

			if (isChatWithAttendantThatCanBeShown) {
				chatGlobalStateStore.chat.add(newChat)

				const shouldNotifyAssignmentAttendance = validateShouldNotifyAttendanceAssignment(newChat)

				if (shouldNotifyAssignmentAttendance) {
					makeAudioNotification()
				}
			}
		})

		socket.onNewClient(async newClient => {
			chatGlobalStateStore.client.setup([newClient])
		})

		socket.onBlockContact(client =>
			handleContactBlockChange({
				client,
				isBlocked: true,
				successMessage: "Contato bloqueado"
			})
		)

		socket.onUnblockContact(client =>
			handleContactBlockChange({
				client,
				isBlocked: false,
				successMessage: "Contato desbloqueado"
			})
		)

		socket.onChatAttendanceTaken(attendance => {
			const isEventEmittedByAnotherUser = attendance.originalEventEmissorUserId !== globalStateStore.user.id
			const isThisAttendanceOpened = attendance.inboxChannelChatId === chatGlobalStateStore.chat.current?.id

			if (isEventEmittedByAnotherUser) {
				chatGlobalStateStore.chat.removeById(attendance.inboxChannelChatId)

				if (isThisAttendanceOpened) {
					chatGlobalStateStore.chat.close()
					Notification.warning({
						message: `O atendente ${attendance.userName} assumiu esse atendimento.`
					})
				}
			} else {
				chatGlobalStateStore.chat.updateById(attendance.inboxChannelChatId, {
					status: "on-going",
					attendance: {
						userName: attendance.userName,
						userId: attendance.userId,
						assignTeamId: attendance.assignTeamId,
						assignUserId: attendance.assignUserId,
						assignmentQueueType: attendance.assignmentQueueType,
						status: "active"
					}
				})
			}
		})

		socket.onChatAttendanceAssigned(attendance => {
			const isCurrentUserResponsability = isCurrentUserResponsibleForAttendance(attendance)

			if (!isCurrentUserResponsability) {
				chatGlobalStateStore.chat.removeById(attendance.inboxChannelChatId)

				const isActualOpenedChat = attendance.inboxChannelChatId === chatGlobalStateStore.chat.currentOpenedId

				if (isActualOpenedChat) {
					chatGlobalStateStore.chat.close()
				}
			}
		})

		socket.onChatAttendanceFinished(attendance => {
			if (isEventOfUnloadedChat(attendance.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(attendance.inboxChannelChatId)

			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				chatGlobalStateStore.chat.updateById(attendance.inboxChannelChatId, {
					status: "archived",
					attendance: {
						userName: "",
						userId: null,
						assignTeamId: null,
						assignUserId: null,
						assignmentQueueType: null,
						status: "finished"
					}
				})
			}
		})

		socket.onNewChatAttendanceNotification(notification => {
			if (isEventOfUnloadedChat(notification.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(notification.inboxChannelChatId)

			if (chat) {
				chatGlobalStateStore.message.add(notification)
			}
		})

		socket.onNewMessage(async message => {
			if (isEventOfUnloadedChat(message.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(message.inboxChannelChatId)

			if (canUpdateWaitingChats(chat.attendance) || isCurrentUserResponsibleForAttendance(chat.attendance)) {
				if (!message.sentByCustomer) {
					chatGlobalStateStore.chat.incrementUnreadMessagesCountById(message.inboxChannelChatId, +1)
				}

				await chatGlobalStateStore.message.add(message)

				const isMessageFromOpenedChat = message.inboxChannelChatId === chatGlobalStateStore.chat.currentOpenedId
				const isMessageSentByCustomer = message.sentByCustomer
				const messageBelongsToFilteredChat = chatGlobalStateStore.chat.filteredChatIds.list.includes(message.inboxChannelChatId)

				if (isMessageFromOpenedChat) {
					chatGlobalStateStore.message.setReadByChatId(message.inboxChannelChatId)
				}

				const isNotActiveWindowMessageNotification = !isMessageSentByCustomer && isMessageFromOpenedChat && !activeWindowListener.active
				const isFilteredChatMessageNotification = !isMessageSentByCustomer && !isMessageFromOpenedChat && messageBelongsToFilteredChat

				const actualUserSettings = userSettings.getUserSettings()
				const recieveSoundNotification = actualUserSettings?.notifications?.soundNotification

				const isChatBotAttendance = chat.status === "chat-bot"

				const notifyByAudio = recieveSoundNotification && (isNotActiveWindowMessageNotification || isFilteredChatMessageNotification) && !isChatBotAttendance

				if (notifyByAudio) {
					makeAudioNotification()
				}
			}
		})

		socket.onMessageUpdated(async message => {
			if (isEventOfUnloadedChat(message.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(message.inboxChannelChatId)

			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				if (!message.sentByCustomer) {
					chatGlobalStateStore.chat.incrementUnreadMessagesCountById(message.inboxChannelChatId, +1)
				}

				await chatGlobalStateStore.message.updateById(message.id, {
					type: message?.type,
					content: message?.content,
					caption: message?.caption
				})
			}
		})

		socket.onAwakeningSnooze((payload) => {
			if (isEventOfUnloadedChat(payload.inboxChannelChatId)) {
				return
			}

			const chat = chatGlobalStateStore.chat.getById(payload.inboxChannelChatId)
			if (isCurrentUserResponsibleForAttendance(chat.attendance)) {
				chatGlobalStateStore.chat.updateById(payload.inboxChannelChatId, {
					status: "on-going",
					attendance: {
						status: "active"
					},
					extraData: {
						awakeningSnooze: true
					}
				})
			}
		})

		socket.onMessageProcessingStepsChanged(payload => {
			const messageId = payload.inboxChannelChatMessageId || payload.inboxChannelChatMessageTempId

			const message = chatGlobalStateStore.message.getById(messageId)

			if (message) {
				chatGlobalStateStore.message.updateById(messageId, {
					inboxChannelChatMessageLog: {
						processingSteps: payload.processingSteps
					}
				})
			}
		})

		wabaChat.setupChatListeners({
			getChatOnStore: chatGlobalStateStore.chat.getById,
			updateChatOnStore: chatGlobalStateStore.chat.updateById
		})
	}

	const setupInitialChatSearch = async (): Promise<{ phoneSearch: string | null; clientId: string | null }> => {
		const {
			phoneSearch,
			isValidPhoneSearch
		} = queryFilter.getChatPhoneSearch()

		const { clientId } = queryFilter.getChatClientIdSearch()

		if (phoneSearch && isValidPhoneSearch) {
			await chatGlobalStateStore.chatListPanel.chatListFilter.update({ text: phoneSearch })
		} else if (clientId) {
			await chatGlobalStateStore.chatListPanel.chatListFilter.update({ text: clientId })
		}

		const hasUrlSearchParam = Boolean(phoneSearch) || Boolean(clientId)
		if (hasUrlSearchParam) {
			history.replace({ search: queryFilter.query.toString() })
		}

		return {
			phoneSearch: isValidPhoneSearch ? phoneSearch : null,
			clientId
		}
	}

	const setupChatByInitialSearch = async (clientId: string | null) => {
		if (!clientId) return

		const clientIdAsNumber = Number(clientId)
		const searchedChat = chatGlobalStateStore.chat.list.find(chat =>
			(chat?.client as Client)?.id === clientIdAsNumber
		)

		if (!searchedChat) {
			await startChatByClientIdFromBackend(clientIdAsNumber)
			return
		}

		const { alwaysAllowed, requiredActions } = chatGlobalStateStore.chat.userHasAccessToChatRules(searchedChat)

		const chatPermissions = globalStateStore?.user?.permissions?.chat
		const hasChatAccess = alwaysAllowed || requiredActions.some(action => chatPermissions?.some(permission => permission.action === action && permission.effect === "allow"))

		if (hasChatAccess) {
			await chatGlobalStateStore.chat.openById(searchedChat.id)
		}
	}

	const initialize = async () => {
		const { phoneSearch, clientId } = await setupInitialChatSearch()

		await Promise.all([
			setupSocket(),
			chatGlobalStateStore.chat.loadAllFromServer(),
			chatGlobalStateStore.quickReply.loadAllFromServer(),
			chatGlobalStateStore.attendant.loadAllFromServer(),
			chatGlobalStateStore.tag.loadAllFromServer(),
			userSettings.getAllUserSettings()
		])

		if (phoneSearch) {
			await openChat({ phone: phoneSearch, clientId })
		} else if (clientId) {
			await setupChatByInitialSearch(clientId)
		}
	}

	useDidMount(() => {
		initialize()
	})

	useEffect(() => {
		const handleOpenChatByPhone = (event: Event) => {
			const customEvent = event as CustomEvent<{ phone?: string; clientId?: number | string }>
			const { phone, clientId } = customEvent.detail || {}

			if (phone || clientId) {
				openChat({ phone, clientId })
			}
		}

		window.addEventListener(INBOX_OPEN_CHAT_BY_PHONE_EVENT, handleOpenChatByPhone)

		return () => {
			window.removeEventListener(INBOX_OPEN_CHAT_BY_PHONE_EVENT, handleOpenChatByPhone)
		}
	}, [openChat])

	useEffect(() => {
		makeVisualNotification()
		// eslint-disable-next-line
	}, [chatGlobalStateStore.chat.currentOpenedId, chatGlobalStateStore.chat.list.length, chatGlobalStateStore.message.list.length, totalUnreadChats])

	return (
		<Grid
			container
			justifyContent="center"
			alignItems="center"
			className={classes.pageContainer}
		>
			<Grid
				container
				className={classes.chatContainer}
			>
				<ChatListPanel />

				<ConversationPanel />
			</Grid>

		</Grid>
	)
}

const ChatWithContext = () => (
	<ChatGlobalStateProvider>
		<Chat />
	</ChatGlobalStateProvider>
)

export default ChatWithContext
